<template>
  <div class="metro-transfer-chart">
    <div v-if="loading" class="loading-state">
      <div class="loading-spinner"></div>
      <p>正在加载换乘数据...</p>
    </div>
    
    <div v-else-if="error" class="error-state">
      <p>{{ error }}</p>
    </div>
    
    <div v-else class="transfer-content">
      <!-- 紧凑型换乘时长排行 -->
      <div class="compact-transfer-table">
        <div class="table-header">
          <div class="header-rank">排名</div>
          <div class="header-station">站点</div>
          <div class="header-lines">换乘线路</div>
          <div class="header-duration">时长</div>
          <div class="header-type">类型</div>
        </div>
        
        <div 
          v-for="(item, index) in transferData" 
          :key="`${item.station}-${index}`"
          class="transfer-row"
          :class="{ 
            'top-rank': index < 3,
            'exit-transfer': item.isExitTransfer
          }"
          :style="{ animationDelay: `${index * 0.05}s` }"
          :title="`点击可跳转至 ${item.station}`"
          @mouseenter="handleStationHover(item.station, $event)"
          @mouseleave="handleStationLeave"
          @click="handleStationClick(item.station)"
        >
          <!-- 排名 -->
          <div class="rank-cell">
            <div class="rank-number" :class="`rank-${index + 1}`">
              {{ index + 1 }}
            </div>
          </div>
          
          <!-- 站点名称 -->
          <div class="station-cell">
            <span class="station-name">{{ item.station }}</span>
          </div>
          
          <!-- 换乘线路 -->
          <div class="lines-cell">
            <div class="lines-compact">
              <!-- 线路A -->
              <div class="line-badges">
                <div 
                  v-for="lineNum in parseLineNumbers(item.lineA)" 
                  :key="`lineA-${lineNum}`"
                  class="line-badge"
                  :style="{ 
                    backgroundColor: getLineColor(lineNum).bg,
                    color: getLineColor(lineNum).text
                  }"
                >
                  {{ lineNum }}
                </div>
              </div>
              
              <div class="transfer-icon">
                <svg width="16" height="12" viewBox="0 0 16 12">
                  <path d="M2 6 L12 6" stroke="#666" stroke-width="1.5" fill="none"/>
                  <path d="M10 3 L13 6 L10 9" stroke="#666" stroke-width="1.5" fill="none"/>
                </svg>
              </div>
              
              <!-- 线路B -->
              <div class="line-badges">
                <div 
                  v-for="lineNum in parseLineNumbers(item.lineB)" 
                  :key="`lineB-${lineNum}`"
                  class="line-badge"
                  :style="{ 
                    backgroundColor: getLineColor(lineNum).bg,
                    color: getLineColor(lineNum).text
                  }"
                >
                  {{ lineNum }}
                </div>
              </div>
            </div>
          </div>
          
          <!-- 换乘时长 -->
          <div class="duration-cell">
            <div class="duration-info">
              <span class="duration-value">{{ item.duration }}</span>
              <span class="duration-unit">分钟</span>
            </div>
            <div class="duration-bar-mini">
              <div 
                class="duration-fill"
                :style="{ 
                  width: getDurationPercentage(item.duration) + '%',
                  backgroundColor: getDurationColor(item.duration, item.isExitTransfer)
                }"
              ></div>
            </div>
          </div>
          
          <!-- 换乘类型 -->
          <div class="type-cell">
            <div class="type-badge" :class="{ 'exit-type': item.isExitTransfer }">
              {{ item.isExitTransfer ? '出站' : '站内' }}
            </div>
          </div>
        </div>
      </div>
      
      <!-- 数据来源注释 -->
      <div class="data-source">
        数据来源：上海地铁微信公众号
      </div>
    </div>
    
    <!-- 悬停弹窗 - 使用 Teleport 移动到 body -->
    <Teleport to="body">
      <div 
        v-if="hoverPopup.rendered" 
        class="station-hover-popup"
        :class="{ 
          'show': hoverPopup.visible,
          'exit-transfer-popup': isExitTransferStation(hoverPopup.stationName)
        }"
        :style="{ 
          left: hoverPopup.x + 'px', 
          top: hoverPopup.y + 'px' 
        }"
        @click="handlePopupClick"
      >
        <div class="popup-header" :class="{ 'exit-transfer-header': isExitTransferStation(hoverPopup.stationName) }">
          <h6 class="popup-title">{{ hoverPopup.stationName }}</h6>
          <div class="popup-subtitle">站点线路图</div>
        </div>
        
        <div class="popup-content">
          <canvas 
            ref="popupCanvasRef"
            :width="popupCanvasSize.width"
            :height="popupCanvasSize.height"
            class="station-canvas"
          />
        </div>
        
        <div class="popup-footer" :class="{ 'exit-transfer-footer': isExitTransferStation(hoverPopup.stationName) }">
          <div class="transfer-hint">
            <span class="hint-text" :class="{ 'exit-hint-text': isExitTransferStation(hoverPopup.stationName) }">{{ isExitTransferStation(hoverPopup.stationName) ? '出站换乘站点' : '站内换乘站点' }}</span>
            <span class="click-hint">点击可跳转至站点</span>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted, nextTick, Teleport, inject } from 'vue'

interface TransferData {
  ranking: number
  station: string
  lineA: string
  lineB: string
  duration: number
  isExitTransfer: boolean
  remarks: string
}

interface StationData {
  name: string
  line: string
  order: number
  lng: number
  lat: number
  x: number
  y: number
  lineColor: string
  fork: number
  isOpened: boolean
}

// 注入跳转方法
const jumpToStation = inject<(stationName: string) => void>('jumpToStation')

const loading = ref(true)
const error = ref('')
const transferData = ref<TransferData[]>([])
const lineColors = ref<any>(null)

// 悬停弹窗状态
const hoverPopup = ref({
  visible: false,
  rendered: false,  // 是否渲染到DOM
  stationName: '',
  x: 0,
  y: 0
})

// 弹窗画布
const popupCanvasRef = ref<HTMLCanvasElement>()
const popupCanvasSize = ref({
  width: 320,
  height: 200  // 减少高度，为header和footer留出空间
})

// 站点数据缓存
const stationsData = ref<StationData[]>([])
const animationId = ref<number>()

// 悬停定时器
let hoverTimer: number | null = null
let hideTimer: number | null = null

// 严格按照MetroSignGenerator的颜色配置
const metroColors: Record<string, { bg: string; text: string }> = {
  '1': { bg: '#E4002B', text: 'white' },
  '2': { bg: '#97D700', text: 'white' },
  '3': { bg: '#FFD100', text: 'black' },
  '4': { bg: '#461D84', text: 'white' },
  '5': { bg: '#944D9A', text: 'white' },
  '6': { bg: '#D40068', text: 'white' },
  '7': { bg: '#ED6F00', text: 'white' },
  '8': { bg: '#0094D8', text: 'white' },
  '9': { bg: '#87CAED', text: 'black' },
  '10': { bg: '#C6AFD4', text: 'black' },
  '11': { bg: '#871C2B', text: 'white' },
  '12': { bg: '#007A60', text: 'white' },
  '13': { bg: '#E999C0', text: 'black' },
  '14': { bg: '#626020', text: 'white' },
  '15': { bg: '#BCA886', text: 'black' },
  '16': { bg: '#98D1C0', text: 'black' },
  '17': { bg: '#BC796F', text: 'white' },
  '18': { bg: '#C4984F', text: 'white' },
  '19': { bg: '#F5AB78', text: 'black' },
  '20': { bg: '#009F65', text: 'white' },
  '21': { bg: '#F7AF00', text: 'black' },
  '22': { bg: '#5F376F', text: 'white' },
  '23': { bg: '#B0D478', text: 'black' },
  '26': { bg: '#5F67A9', text: 'white' }
}

// 获取线路颜色和文字颜色
const getLineColor = (lineNumber: string): { bg: string; text: string } => {
  if (!lineNumber) return { bg: '#999999', text: 'white' }
  
  const colorConfig = metroColors[lineNumber]
  if (colorConfig) {
    return colorConfig
  }
  
  return { bg: '#999999', text: 'white' }
}

// 解析线路名称为数字数组（处理"3/4号线"这样的组合）
const parseLineNumbers = (lineName: string): string[] => {
  if (!lineName) return []
  
  // 移除"号线"后缀
  const cleanName = lineName.replace(/号线/g, '')
  
  // 检查是否包含"/"分隔符
  if (cleanName.includes('/')) {
    return cleanName.split('/').map(num => num.trim()).filter(num => num)
  }
  
  // 提取单个线路号码
  const lineNumber = cleanName.replace(/[^\d]/g, '')
  return lineNumber ? [lineNumber] : []
}

// 计算时长百分比（用于进度条）
const getDurationPercentage = (duration: number): number => {
  const maxDuration = Math.max(...transferData.value.map(item => item.duration))
  return (duration / maxDuration) * 100
}

// 根据时长获取颜色
const getDurationColor = (duration: number, isExitTransfer: boolean): string => {
  // 出站换乘使用红色系
  if (isExitTransfer) {
    return '#dc2626'
  }
  
  // 站内换乘使用蓝绿色系，根据时长区分深浅
  if (duration >= 9) return '#dc2626' // 9分钟及以上用红色表示较长
  if (duration >= 7) return '#f59e0b' // 7-8分钟用橙色
  if (duration >= 6) return '#0891b2' // 6分钟用蓝色
  return '#06b6d4' // 6分钟以下用浅蓝色
}

// 获取地铁线路颜色
const getMetroLineColor = (lineName: string): string => {
  const colors: Record<string, string> = {
    '1号线': '#E3002B',
    '2号线': '#82BF25',
    '3号线': '#FCD600',
    '4号线': '#461D84',
    '5号线': '#944D9A',
    '6号线': '#D40068',
    '7号线': '#ED6F00',
    '8号线': '#0094D8',
    '9号线': '#87CAED',
    '10号线': '#C6AFD4',
    '11号线': '#871C2B',
    '12号线': '#007A60',
    '13号线': '#E999C0',
    '14号线': '#626020',
    '15号线': '#BCA886',
    '16号线': '#98D1C0',
    '17号线': '#BC796F',
    '18号线': '#C4984F',
    '19号线': '#F5AB78',
    '20号线': '#009F65',
    '21号线': '#F7AF00',
    '22号线': '#5F376F',
    '23号线': '#B0D478',
    '26号线': '#5F67A9',
  }
  return colors[lineName] || '#64748b'
}

// 坐标转换函数
const convertCoordinates = (lng: number, lat: number, bounds: any) => {
  const lngRange = bounds.maxLng - bounds.minLng
  const latRange = bounds.maxLat - bounds.minLat
  
  const scaleFactor = 2.0
  const margin = 20
  
  const scaledWidth = popupCanvasSize.value.width * scaleFactor
  const scaledHeight = popupCanvasSize.value.height * scaleFactor
  
  const normalizedX = (lng - bounds.minLng) / lngRange
  const normalizedY = (lat - bounds.minLat) / latRange
  
  const x = normalizedX * (scaledWidth - 2 * margin) + margin - (scaledWidth - popupCanvasSize.value.width) / 2
  const y = scaledHeight - (normalizedY * (scaledHeight - 2 * margin) + margin) - (scaledHeight - popupCanvasSize.value.height) / 2
  
  return { x, y }
}

// 绘制换乘站连接线
const drawTransferConnections = (ctx: CanvasRenderingContext2D, stations: StationData[]) => {
  if (stations.length < 2) return
  
  // 设置连接线样式
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)'
  ctx.lineWidth = 2
  ctx.lineCap = 'round'
  ctx.globalAlpha = 0.6
  
  // 如果只有两个站点，直接连接
  if (stations.length === 2) {
    ctx.beginPath()
    ctx.moveTo(stations[0].x, stations[0].y)
    ctx.lineTo(stations[1].x, stations[1].y)
    ctx.stroke()
  } else {
    // 多个站点：连接到中心点
    const centerX = stations.reduce((sum, s) => sum + s.x, 0) / stations.length
    const centerY = stations.reduce((sum, s) => sum + s.y, 0) / stations.length
    
    ctx.beginPath()
    for (const station of stations) {
      ctx.moveTo(station.x, station.y)
      ctx.lineTo(centerX, centerY)
    }
    ctx.stroke()
  }
  
  ctx.globalAlpha = 1
}

// 绘制换乘站统一站名
const drawTransferStationName = (ctx: CanvasRenderingContext2D, station: StationData, isTarget: boolean) => {
  // 站点名称（换乘站只显示一个名称）
  ctx.fillStyle = isTarget ? '#fecaca' : '#ffffff'
  ctx.font = isTarget ? 'bold 12px -apple-system, BlinkMacSystemFont, "Segoe UI", "Microsoft YaHei", sans-serif' : '11px -apple-system, BlinkMacSystemFont, "Segoe UI", "Microsoft YaHei", sans-serif'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'top'
  ctx.globalAlpha = 1
  
  // 添加文字描边
  ctx.strokeStyle = isTarget ? '#7f1d1d' : '#000000'
  ctx.lineWidth = isTarget ? 3 : 2
  
  // 计算文字位置（站点下方）
  const outerSize = isTarget ? 8 : 5
  const textY = station.y + outerSize + 3
  
  ctx.strokeText(station.name, station.x, textY)
  ctx.fillText(station.name, station.x, textY)
}

// 出站换乘站点列表
const exitTransferStations = ['南京西路', '曹杨路']

// 检查是否为出站换乘站点
const isExitTransferStation = (stationName: string): boolean => {
  return exitTransferStations.some(exitStation => 
    stationName.includes(exitStation) || exitStation.includes(stationName)
  )
}

// 悬停处理函数
const handleStationHover = (stationName: string, event: MouseEvent) => {
  // 清除所有之前的定时器
  if (hoverTimer) {
    clearTimeout(hoverTimer)
    hoverTimer = null
  }
  if (hideTimer) {
    clearTimeout(hideTimer)
    hideTimer = null
  }
  
  // 如果已经在显示相同站点的弹窗，直接返回
  if (hoverPopup.value.visible && hoverPopup.value.stationName === stationName) {
    return
  }
  
  // 如果正在显示其他站点的弹窗，立即隐藏
  if (hoverPopup.value.visible || hoverPopup.value.rendered) {
    hoverPopup.value.visible = false
    hoverPopup.value.rendered = false
    
    // 停止之前的动画
    if (animationId.value) {
      cancelAnimationFrame(animationId.value)
      animationId.value = undefined
    }
  }
  
  // 设置防抖，200ms后显示新弹窗
  hoverTimer = setTimeout(() => {
    const rect = (event.target as HTMLElement).getBoundingClientRect()
    const popupWidth = 320
    const popupHeight = 260  // 调整高度以适应新的canvas尺寸
    
    // 计算弹窗位置，确保不超出屏幕边界
    let x = rect.right + 10
    let y = rect.top - 50
    
    // 检查右边界
    if (x + popupWidth > window.innerWidth) {
      x = rect.left - popupWidth - 10 // 在左侧显示
    }
    
    // 检查下边界
    if (y + popupHeight > window.innerHeight) {
      y = window.innerHeight - popupHeight - 20
    }
    
    // 检查上边界
    if (y < 10) {
      y = 10
    }
    
    hoverPopup.value = {
      visible: false,  // 先设为false，准备动画
      rendered: true,  // 渲染到DOM
      stationName,
      x: Math.max(10, x),
      y: Math.max(10, y)
    }
    
    // 等待DOM更新后开始动画
    nextTick(() => {
      // 短暂延迟后设置visible为true，触发show类
      setTimeout(() => {
        // 再次检查是否还需要显示（用户可能已经移开鼠标）
        if (hoverPopup.value.rendered && hoverPopup.value.stationName === stationName) {
          hoverPopup.value.visible = true
          startPopupAnimation(stationName)
        }
      }, 10)
    })
  }, 200)
}

const handleStationLeave = () => {
  // 清除所有定时器
  if (hoverTimer) {
    clearTimeout(hoverTimer)
    hoverTimer = null
  }
  if (hideTimer) {
    clearTimeout(hideTimer)
    hideTimer = null
  }
  
  // 如果弹窗正在显示，开始淡出动画
  if (hoverPopup.value.visible || hoverPopup.value.rendered) {
    hoverPopup.value.visible = false
    
    // 延迟隐藏，等待动画完成
    hideTimer = setTimeout(() => {
      hoverPopup.value.rendered = false
    }, 300) // 与CSS动画时间一致
  }
  
  // 停止动画
  if (animationId.value) {
    cancelAnimationFrame(animationId.value)
    animationId.value = undefined
  }
}

// 处理弹窗点击事件
const handlePopupClick = () => {
  if (!jumpToStation) {
    return
  }
  
  const stationName = hoverPopup.value.stationName
  if (stationName) {
    jumpToStation(stationName)
    
    // 隐藏弹窗
    handleStationLeave()
  }
}

// 开始弹窗动画
const startPopupAnimation = (stationName: string) => {
  if (!popupCanvasRef.value) return
  
  const animate = (time: number) => {
    drawStationMap(stationName, time)
    if (hoverPopup.value.visible) {
      animationId.value = requestAnimationFrame(animate)
    }
  }
  
  animationId.value = requestAnimationFrame(animate)
}

// 绘制站点周围地图
const drawStationMap = (stationName: string, time: number) => {
  const canvas = popupCanvasRef.value
  if (!canvas) return
  
  const ctx = canvas.getContext('2d')
  if (!ctx) return
  
  // 清空画布
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  
  // 绘制深红色背景
  ctx.fillStyle = '#1a0a0a'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  
  // 找到目标站点及其周围站点
  const targetStations = stationsData.value.filter(s => s.name.includes(stationName) || stationName.includes(s.name))
  
  if (targetStations.length === 0) {
    // 如果没有找到精确匹配，绘制提示信息
    ctx.fillStyle = '#fecaca'
    ctx.font = '14px sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText('未找到站点数据', canvas.width / 2, canvas.height / 2)
    ctx.fillText(stationName, canvas.width / 2, canvas.height / 2 + 20)
    return
  }
  
  const targetStation = targetStations[0]
  const nearbyStations: StationData[] = []
  
  // 找到相同线路和相邻站点（减少到2个站的距离）
  for (const station of stationsData.value) {
    // 同名站点（换乘站）
    if (station.name.includes(stationName) || stationName.includes(station.name)) {
      nearbyStations.push(station)
      continue
    }
    
    // 相同线路的相邻站点（减少到2个站的距离，显示更少站点）
    for (const target of targetStations) {
      if (station.line === target.line && 
          Math.abs(station.order - target.order) <= 2) {
        nearbyStations.push(station)
      }
    }
  }
  
  if (nearbyStations.length === 0) {
    ctx.fillStyle = '#fecaca'
    ctx.font = '14px sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText('无周围站点数据', canvas.width / 2, canvas.height / 2)
    return
  }
  
  // 重新计算坐标以适合弹窗尺寸
  const bounds = {
    minLng: Math.min(...nearbyStations.map(s => s.lng)),
    maxLng: Math.max(...nearbyStations.map(s => s.lng)),
    minLat: Math.min(...nearbyStations.map(s => s.lat)),
    maxLat: Math.max(...nearbyStations.map(s => s.lat))
  }
  
  // 为每个站点计算在弹窗中的坐标
  const margin = 30
  const drawWidth = canvas.width - 2 * margin
  const drawHeight = canvas.height - 2 * margin
  
  for (const station of nearbyStations) {
    const normalizedX = (station.lng - bounds.minLng) / (bounds.maxLng - bounds.minLng || 1)
    const normalizedY = (station.lat - bounds.minLat) / (bounds.maxLat - bounds.minLat || 1)
    
    station.x = margin + normalizedX * drawWidth
    station.y = canvas.height - margin - normalizedY * drawHeight
  }
  
  // 按线路分组
  const lineGroups = new Map<string, StationData[]>()
  for (const station of nearbyStations) {
    if (!lineGroups.has(station.line)) {
      lineGroups.set(station.line, [])
    }
    lineGroups.get(station.line)!.push(station)
  }
  
  // 绘制线路 - 采用与主线路图一致的样式
  for (const [lineName, stations] of lineGroups) {
    if (stations.length < 2) continue
    
    stations.sort((a, b) => a.order - b.order)
    
    // 创建线路路径
    const linePath = new Path2D()
    linePath.moveTo(stations[0].x, stations[0].y)
    for (let i = 1; i < stations.length; i++) {
      linePath.lineTo(stations[i].x, stations[i].y)
    }
    
    // 先绘制轻微的白色阴影（与主线路图一致）
    ctx.strokeStyle = '#ffffff'
    ctx.lineWidth = 5
    ctx.globalAlpha = 0.2
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.stroke(linePath)
    
    // 绘制主线条（与主线路图一致）
    ctx.strokeStyle = getMetroLineColor(lineName)
    ctx.lineWidth = 4
    ctx.globalAlpha = 1
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.stroke(linePath)
  }
  
  // 绘制站点 - 采用与主线路图一致的样式
  const pulse = Math.sin(time * 0.005) * 0.5 + 0.5
  
  // 处理换乘站：按站名分组
  const stationGroups = new Map<string, StationData[]>()
  for (const station of nearbyStations) {
    if (!stationGroups.has(station.name)) {
      stationGroups.set(station.name, [])
    }
    stationGroups.get(station.name)!.push(station)
  }
  
  // 绘制所有站点圆圈
  for (const station of nearbyStations) {
    const isTarget = station.name.includes(stationName) || stationName.includes(station.name)
    
    // 基础大小（与主线路图一致）
    let stationSize = 3
    let outerSize = stationSize + 2
    
    // 目标站点的悬停效果
    if (isTarget) {
      stationSize = 5
      outerSize = stationSize + 3
      
      // 添加发光效果（与主线路图一致）
      const glowSize = outerSize + (pulse * 3)
      ctx.fillStyle = station.lineColor
      ctx.globalAlpha = 0.3 * (1 - pulse * 0.5)
      ctx.beginPath()
      ctx.arc(station.x, station.y, glowSize, 0, Math.PI * 2)
      ctx.fill()
    }
    
    // 站点外圈（与主线路图一致）
    ctx.fillStyle = '#ffffff'
    ctx.globalAlpha = isTarget ? 1 : 0.9
    ctx.beginPath()
    ctx.arc(station.x, station.y, outerSize, 0, Math.PI * 2)
    ctx.fill()
    
    // 站点内圈（与主线路图一致）
    ctx.fillStyle = station.lineColor
    ctx.globalAlpha = 1
    ctx.beginPath()
    ctx.arc(station.x, station.y, stationSize, 0, Math.PI * 2)
    ctx.fill()
  }
  
  // 绘制换乘站连接线和统一站名
  for (const [stationNameKey, stationList] of stationGroups) {
    const isTarget = stationNameKey.includes(stationName) || stationName.includes(stationNameKey)
    
    if (stationList.length > 1) {
      // 这是换乘站，绘制连接线
      drawTransferConnections(ctx, stationList)
      // 只绘制一个站名（选择第一个站点的位置）
      drawTransferStationName(ctx, stationList[0], isTarget)
    } else {
      // 单线路站点，正常绘制站名
      drawTransferStationName(ctx, stationList[0], isTarget)
    }
  }
  
  ctx.globalAlpha = 1
}

// 加载站点数据用于绘制线路图
const loadStationData = async () => {
  try {
    const response = await fetch('/metro_station_location.csv')
    if (!response.ok) throw new Error('站点数据加载失败')
    
    const csvText = await response.text()
    const lines = csvText.split('\n').slice(1) // 跳过标题行
    const stations: StationData[] = []
    
    // 计算坐标边界
    let minLng = Infinity, maxLng = -Infinity
    let minLat = Infinity, maxLat = -Infinity
    
    for (const line of lines) {
      if (!line.trim()) continue
      
      const fields = []
      let current = ''
      let inQuotes = false
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i]
        if (char === '"') {
          inQuotes = !inQuotes
        } else if (char === ',' && !inQuotes) {
          fields.push(current.trim())
          current = ''
        } else {
          current += char
        }
      }
      fields.push(current.trim())
      
      if (fields.length < 6) continue
      
      const [sname, rname, order, lat, lng, fork] = fields
      const latValue = parseFloat(lat.replace(/"/g, ''))
      const lngValue = parseFloat(lng.replace(/"/g, ''))
      
      if (isNaN(latValue) || isNaN(lngValue)) continue
      
      minLng = Math.min(minLng, lngValue)
      maxLng = Math.max(maxLng, lngValue)
      minLat = Math.min(minLat, latValue)
      maxLat = Math.max(maxLat, latValue)
      
      const lineMatch = rname.replace(/"/g, '').match(/(\d+)号线|(\S+线)/)
      if (!lineMatch) continue
      
      const lineName = lineMatch[1] ? `${lineMatch[1]}号线` : lineMatch[2]
      
      stations.push({
        name: sname.replace(/"/g, '').replace('(地铁站)', ''),
        line: lineName,
        order: parseFloat(order.replace(/"/g, '')),
        lng: lngValue,
        lat: latValue,
        x: 0, // 稍后计算
        y: 0, // 稍后计算
        lineColor: getMetroLineColor(lineName),
        fork: parseInt(fork.replace(/"/g, '')),
        isOpened: true // 简化处理
      })
    }
    
    // 计算屏幕坐标
    const bounds = { minLng, maxLng, minLat, maxLat }
    for (const station of stations) {
      const coords = convertCoordinates(station.lng, station.lat, bounds)
      station.x = coords.x
      station.y = coords.y
    }
    
    stationsData.value = stations
  } catch (err) {
    // Error handled silently
  }
}

// 解析CSV数据
const parseTransferCSV = (csvText: string): TransferData[] => {
  const lines = csvText.trim().split('\n')
  const data: TransferData[] = []
  
  for (let i = 1; i < lines.length; i++) { // 跳过表头
    const line = lines[i]
    if (!line.trim()) continue
    
    // 处理带引号的CSV字段
    const fields: string[] = []
    let current = ''
    let inQuotes = false
    
    for (let j = 0; j < line.length; j++) {
      const char = line[j]
      if (char === '"') {
        inQuotes = !inQuotes
      } else if (char === ',' && !inQuotes) {
        fields.push(current.trim())
        current = ''
      } else {
        current += char
      }
    }
    fields.push(current.trim()) // 添加最后一个字段
    
    if (fields.length >= 4) {
      const station = fields[0] || ''
      const lineA = fields[1] || ''
      const lineB = fields[2] || ''
      const duration = parseInt(fields[3]?.replace('约', '')) || 0
      
      if (station && lineA && lineB && duration > 0) {
        // 检查是否为出站换乘站点
        const isExitTransfer = exitTransferStations.includes(station)
        
        data.push({
          ranking: 0, // 将在排序后设置
          station,
          lineA,
          lineB,
          duration,
          isExitTransfer,
          remarks: ''
        })
      }
    }
  }
  
  // 按时长降序排序并设置排名
  const sortedData = data.sort((a, b) => b.duration - a.duration)
  sortedData.forEach((item, index) => {
    item.ranking = index + 1
  })
  
  return sortedData
}

// 加载数据
const loadData = async () => {
  try {
    loading.value = true
    
    // 同时加载换乘数据和颜色数据
    const [transferResponse, colorsResponse] = await Promise.all([
      fetch('/transfer_length.csv'),
      fetch('/shanghai-metro-colors.json')
    ])
    
    if (!transferResponse.ok) {
      throw new Error('换乘数据加载失败')
    }
    
    const transferText = await transferResponse.text()
    transferData.value = parseTransferCSV(transferText)
    
    // 颜色数据加载失败不影响主功能
    if (colorsResponse.ok) {
      lineColors.value = await colorsResponse.json()
    }
    
    if (transferData.value.length === 0) {
      throw new Error('没有有效的换乘数据')
    }
    
    // 加载站点数据用于弹窗显示
    await loadStationData()
    
  } catch (err) {
    error.value = `数据加载失败: ${err}`
  } finally {
    loading.value = false
  }
}

// 处理站点点击事件
const handleStationClick = (stationName: string) => {
  if (jumpToStation) {
    jumpToStation(stationName)
  }
}

onMounted(() => {
  loadData()
})

onUnmounted(() => {
  // 清理所有定时器
  if (hoverTimer) {
    clearTimeout(hoverTimer)
  }
  if (hideTimer) {
    clearTimeout(hideTimer)
  }
  if (animationId.value) {
    cancelAnimationFrame(animationId.value)
  }
})
</script>

<style scoped>
.metro-transfer-chart {
  position: relative;
  width: 100%;
  padding: 20px;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  /* 确保弹窗不被隐藏 */
  overflow: visible;
}

/* 悬停弹窗样式 */
.station-hover-popup {
  position: fixed;
  z-index: 99999;
  background: linear-gradient(135deg, #1a0a0a 0%, #2a1010 100%);
  border-radius: 16px;
  box-shadow: 
    0 20px 40px rgba(59, 130, 246, 0.4),
    0 0 0 1px rgba(59, 130, 246, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.3);
  overflow: hidden;
  pointer-events: auto; /* 允许点击 */
  cursor: pointer; /* 鼠标指针 */
  width: 320px;
  height: 260px;
  /* 初始隐藏状态 */
  opacity: 0;
  transform: scale(0.9) translateY(10px);
  transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1), 
              transform 0.3s cubic-bezier(0.4, 0, 0.2, 1),
              box-shadow 0.2s ease;
}

/* hover效果 */
.station-hover-popup:hover {
  box-shadow: 
    0 25px 50px rgba(59, 130, 246, 0.5),
    0 0 0 1px rgba(59, 130, 246, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.15);
  border-color: rgba(59, 130, 246, 0.4);
}

/* 出站换乘弹窗样式 - 红色系 */
.station-hover-popup.exit-transfer-popup {
  box-shadow: 
    0 20px 40px rgba(220, 38, 38, 0.4),
    0 0 0 1px rgba(220, 38, 38, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(220, 38, 38, 0.3);
}

/* 出站换乘弹窗hover效果 - 红色系 */
.station-hover-popup.exit-transfer-popup:hover {
  box-shadow: 
    0 25px 50px rgba(220, 38, 38, 0.5),
    0 0 0 1px rgba(220, 38, 38, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.15);
  border-color: rgba(220, 38, 38, 0.4);
}

/* 显示状态 */
.station-hover-popup.show {
  opacity: 1;
  transform: scale(1) translateY(0);
}

@keyframes popupFadeIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.popup-header {
  padding: 16px 20px 12px 20px;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%); /* 蓝色渐变 */
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

/* 出站换乘弹窗header红色渐变 */
.popup-header.exit-transfer-header {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%); /* 红色渐变 */
}

.popup-title {
  font-size: 16px;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 4px 0;
  letter-spacing: 0.25px;
}

.popup-subtitle {
  font-size: 12px;
  color: #bfdbfe; /* 浅蓝色 */
  margin: 0;
  font-weight: 400;
}

.popup-content {
  padding: 0;
  background: #1a0a0a; /* 深色背景 */
}

.station-canvas {
  display: block;
  width: 100%;
  height: 100%;
  border: none;
}

.popup-footer {
  padding: 12px 20px 16px 20px;
  background: linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%); /* 深蓝色渐变 */
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

/* 出站换乘弹窗footer红色渐变 */
.popup-footer.exit-transfer-footer {
  background: linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%); /* 深红色渐变 */
}

.transfer-hint {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.hint-text {
  font-size: 11px;
  color: #bfdbfe; /* 浅蓝色文字 */
  font-weight: 500;
  padding: 4px 8px;
  background: rgba(59, 130, 246, 0.2); /* 蓝色半透明背景 */
  border-radius: 4px;
  border: 1px solid rgba(59, 130, 246, 0.3);
}

/* 出站换乘提示文字红色样式 */
.hint-text.exit-hint-text {
  color: #fecaca; /* 浅红色文字 */
  background: rgba(220, 38, 38, 0.2); /* 红色半透明背景 */
  border: 1px solid rgba(220, 38, 38, 0.3);
}

.click-hint {
  font-size: 10px;
  color: #f3f4f6;
  opacity: 0.8;
  font-style: italic;
}

/* 数据来源注释 */
.data-source {
  position: absolute;
  bottom: 12px;
  right: 16px;
  font-size: 12px;
  color: #64748b;
  background: rgba(255, 255, 255, 0.9);
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid rgba(100, 116, 139, 0.2);
  backdrop-filter: blur(4px);
  z-index: 10;
  font-family: 'Noto Sans SC', 'Microsoft YaHei', sans-serif;
  font-weight: 400;
  letter-spacing: 0.2px;
}

/* 加载和错误状态 */
.loading-state,
.error-state {
  text-align: center;
  padding: 40px;
  color: #64748b;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e2e8f0;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* 紧凑型表格样式 */
.compact-transfer-table {
  margin-bottom: 40px;
  border-radius: 12px;
  overflow: hidden;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.table-header {
  display: grid;
  grid-template-columns: 50px 120px 1fr 80px 60px;
  gap: 12px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 0.5px;
}

.header-rank,
.header-station,
.header-lines,
.header-duration,
.header-type {
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-lines {
  justify-content: center;
}

/* 换乘行样式 */
.transfer-row {
  display: grid;
  grid-template-columns: 50px 120px 1fr 80px 60px;
  gap: 12px;
  padding: 12px 20px;
  align-items: center;
  border-bottom: 1px solid #f1f5f9;
  transition: all 0.3s ease;
  opacity: 0;
  transform: translateY(10px);
  animation: fadeInUp 0.4s ease forwards;
  cursor: pointer;
}

.transfer-row:hover {
  background: rgba(59, 130, 246, 0.08);
  transform: translateY(-2px) scale(1.01);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.transfer-row.top-rank {
  background: linear-gradient(90deg, rgba(245, 158, 11, 0.1) 0%, rgba(245, 158, 11, 0.05) 100%);
  border-left: 3px solid #f59e0b;
}

.transfer-row.exit-transfer {
  background: linear-gradient(90deg, rgba(220, 38, 38, 0.1) 0%, rgba(220, 38, 38, 0.05) 100%);
  border-left: 3px solid #dc2626;
}

/* 为非前三名的站内换乘添加左边装饰条 */
.transfer-row:not(.top-rank):not(.exit-transfer) {
  border-left: 3px solid #06b6d4;
  background: linear-gradient(90deg, rgba(6, 182, 212, 0.08) 0%, rgba(6, 182, 212, 0.03) 100%);
}

@keyframes fadeInUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 排名单元格 */
.rank-cell {
  display: flex;
  justify-content: center;
  align-items: center;
}

.rank-number {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
  color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.rank-number.rank-1 { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); }
.rank-number.rank-2 { background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%); }
.rank-number.rank-3 { background: linear-gradient(135deg, #cd7c0a 0%, #92400e 100%); }
.rank-number:not(.rank-1):not(.rank-2):not(.rank-3) { 
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%); 
}

/* 站点单元格 */
.station-cell {
  display: flex;
  align-items: center;
  justify-content: center;
}

.station-name {
  font-weight: 600;
  color: #1f2937;
  font-size: 14px;
  text-align: center;
}

/* 线路单元格 */
.lines-cell {
  display: flex;
  align-items: center;
  justify-content: center;
}

.lines-compact {
  display: flex;
  align-items: center;
  gap: 8px;
}

.line-badges {
  display: flex;
  gap: 4px;
}

.line-badge {
  min-width: 24px;
  height: 24px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  padding: 0 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s ease;
}

.line-badge:hover {
  transform: scale(1.05);
}

.transfer-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.6;
}

/* 时长单元格 */
.duration-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.duration-info {
  display: flex;
  align-items: baseline;
  gap: 2px;
}

.duration-value {
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
}

.duration-unit {
  font-size: 11px;
  color: #6b7280;
}

.duration-bar-mini {
  width: 50px;
  height: 4px;
  background: #e5e7eb;
  border-radius: 2px;
  overflow: hidden;
}

.duration-fill {
  height: 100%;
  border-radius: 2px;
  transition: width 0.6s ease-out;
}

/* 类型单元格 */
.type-cell {
  display: flex;
  justify-content: center;
  align-items: center;
}

.type-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
  color: white;
  box-shadow: 0 1px 3px rgba(6, 182, 212, 0.3);
}

.type-badge.exit-type {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  box-shadow: 0 1px 3px rgba(220, 38, 38, 0.3);
}

/* 响应式设计 */
@media (max-width: 768px) {
  .compact-transfer-table {
    margin: 0 -10px 40px -10px;
    border-radius: 0;
  }
  
  .table-header,
  .transfer-row {
    grid-template-columns: 40px 100px 1fr 70px 50px;
    gap: 8px;
    padding: 12px 16px;
  }
  
  .table-header {
    font-size: 12px;
  }
  
  .rank-number {
    width: 24px;
    height: 24px;
    font-size: 12px;
  }
  
  .station-name {
    font-size: 13px;
  }
  
  .line-badge {
    min-width: 20px;
    height: 20px;
    font-size: 10px;
    padding: 0 4px;
  }
  
  .duration-value {
    font-size: 14px;
  }
  
  .duration-unit {
    font-size: 10px;
  }
  
  .duration-bar-mini {
    width: 40px;
    height: 3px;
  }
  
  .type-badge {
    padding: 3px 6px;
    font-size: 10px;
  }
}
</style>